import { addLocaleData, LocaleData } from 'react-intl';
import en from 'react-intl/locale-data/en';

import { browserLocales } from '../facade/window';

// Always load en locale data
addLocaleData(en);

const defaultLocale = 'en';

const localesConfig = [
  {
    loader: () => import(/* webpackChunkName: "i18n_cs" */ './loaders/cs'),
    locales: ['cs', 'cs-CZ'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_da" */ './loaders/da'),
    locales: ['da', 'da-DK'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_de" */ './loaders/de'),
    locales: ['de', 'de-DE'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_es" */ './loaders/es'),
    locales: ['es', 'es-ES'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_et" */ './loaders/et'),
    locales: ['et', 'et-EE'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_fi" */ './loaders/fi'),
    locales: ['fi', 'fi-FI'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_fr" */ './loaders/fr'),
    locales: ['fr', 'fr-FR'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_hu" */ './loaders/hu'),
    locales: ['hu', 'hu-HU'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_it" */ './loaders/it'),
    locales: ['it', 'it-IT'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_ja" */ './loaders/ja'),
    locales: ['ja', 'ja-JP'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_ko" */ './loaders/ko'),
    locales: ['ko', 'ko-KR'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_nl" */ './loaders/nl'),
    locales: ['nl', 'nl-NL'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_nb" */ './loaders/nb'),
    locales: ['nb', 'nb-NO'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_pl" */ './loaders/pl'),
    locales: ['pl', 'pl-PL'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_ptbr" */ './loaders/ptbr'),
    locales: ['pt-BR'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_pt" */ './loaders/pt'),
    locales: ['pt', 'pt-PT'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_ru" */ './loaders/ru'),
    locales: ['ru', 'ru-RU'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_sk" */ './loaders/sk'),
    locales: ['sk', 'sk-SK'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_sv" */ './loaders/sv'),
    locales: ['sv', 'sv-SE'],
  },
  {
    loader: () => import(/* webpackChunkName: "i18n_zh" */ './loaders/zh'),
    locales: ['zh', 'zh-CN'],
  },
];

interface Translations {
  [key: string]: string;
}

type Loader = () => Promise<{
  localeData: LocaleData;
  translations: Translations;
}>;

// Generated list of supported locales based on locales config
const supportedLocales = localesConfig.reduce((memo, config) => [...memo, ...config.locales], [defaultLocale]);

// Generated list of locale loaders
const localeLoaders: { [locale: string]: Loader } = localesConfig.reduce((memo: { [key: string]: Loader }, config) => {
  config.locales.forEach(locale => (memo[locale] = config.loader));
  return memo;
}, {});

function getSupportedLocale(locale?: string): string | false {
  if (locale) {
    locale = locale.replace('_', '-');
    if (supportedLocales.indexOf(locale) !== -1) {
      return locale;
    }

    // If we don't have the exact locale available, attempt to match just the language
    const languageCode = locale.substring(0, 2);
    if (supportedLocales.indexOf(languageCode) !== -1) {
      return languageCode;
    }
  }

  return false;
}

// Returns either a locale code ('en-US') or two letter language code ('en')
export function determineLocale(userLocale?: string): string {
  for (const locale of [userLocale, ...browserLocales()]) {
    const supportedLocale = getSupportedLocale(locale);
    if (supportedLocale) {
      return supportedLocale;
    }
  }

  // Default to English
  return defaultLocale;
}

/**
 * Load the locale data for the current locale, returning the messages
 */
export function setUpForLocale(locale: string): Promise<Translations> {
  if (locale === defaultLocale) {
    // Everything is already loaded for English
    return Promise.resolve({});
  }

  return new Promise((resolve, reject) => {
    // Fail if we haven't loaded/resolved after 30 seconds
    setTimeout(reject, 30000);

    if (localeLoaders[locale]) {
      return localeLoaders[locale]().then(({ translations, localeData }) => {
        addLocaleData(localeData);
        resolve(translations);
      });
    }

    // no translations, fall back to english
    // tslint:disable-next-line no-console
    console.log(`Programmer error: missing mapping for locale ${locale}`);
    resolve({});
  });
}
